import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import {
  FileCheck, ArrowRight, Sparkles,
  UserCheck, Video, BadgeCheck, Info,
  Shield, Eye, Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';

const steps = [
  {
    icon: FileCheck,
    title: "Submit Your Profile",
    desc: "Complete your teaching profile — expertise, qualifications, offerings, and pricing — then submit it for review.",
    gradient: "from-[hsl(237,52%,40%)] to-[hsl(237,52%,31%)]",
  },
  {
    icon: UserCheck,
    title: "Admin Review",
    desc: "A member of our team manually reviews your profile for completeness, accuracy, and quality before approving it.",
    gradient: "from-[hsl(39,96%,48%)] to-[hsl(30,90%,45%)]",
  },
  {
    icon: Video,
    title: "Video Identity Check",
    desc: "We schedule a short video call to confirm your identity matches your profile. This is what makes our Verified badge meaningful.",
    gradient: "from-[hsl(260,60%,55%)] to-[hsl(280,55%,50%)]",
  },
  {
    icon: BadgeCheck,
    title: "Go Live with a Verified Badge",
    desc: "Once approved, your profile goes live with a Verified badge — boosting your credibility and making you more discoverable to students.",
    gradient: "from-[hsl(152,60%,42%)] to-[hsl(152,55%,35%)]",
  },
];

const benefits = [
  { icon: Shield, title: "Student trust",         desc: "The Verified badge signals to every student that you've been personally vetted by our team." },
  { icon: Eye,    title: "Better discoverability", desc: "Verified teachers appear more prominently in search results and category listings." },
  { icon: Star,   title: "Eligible for reviews",   desc: "Only verified teachers can receive student reviews — reviews drive long-term growth." },
];

export default function HowItWorksVerificationPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const title = 'Teacher Verification | BookMyCoaching';
  const description = 'BookMyCoaching personally verifies every teacher — identity, qualifications, and profile quality — before they appear in search results.';
  const canonicalUrl = getCanonicalUrl(pathname);
  const ogImage = getDefaultOgImage();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
      </Helmet>
      <Header />

      <main>

        {/* ── Hero ── */}
        <section className="relative pt-28 pb-10 sm:pt-44 sm:pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[hsl(237,52%,92%)]/80 via-[hsl(237,45%,90%)]/60 to-[hsl(39,96%,93%)]/50" />
          <svg className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="verify-grid" x="0" y="0" width="44" height="44" patternUnits="userSpaceOnUse">
                <path d="M 44 0 L 0 0 0 44" fill="none" stroke="hsl(237,52%,31%)" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#verify-grid)" />
          </svg>
          <div className="hidden sm:block absolute -top-20 -right-20 w-[500px] h-[500px] bg-white/40 rounded-full blur-[100px] pointer-events-none" />
          <div className="hidden sm:block absolute bottom-0 left-[10%] w-[400px] h-[300px] bg-[hsl(39,96%,72%)]/15 rounded-full blur-[100px] pointer-events-none" />

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/80 border border-[hsl(237,52%,31%)]/20 shadow-sm mb-5">
                <Sparkles className="w-3.5 h-3.5 text-[hsl(39,96%,48%)]" />
                <span className="text-xs font-semibold text-[#262A7A] tracking-wide">For Teachers · Verification</span>
              </div>

              <h1 className="font-sans text-[1.75rem] sm:text-5xl lg:text-[3.25rem] font-extrabold text-[#262A7A] leading-[1.1] tracking-tight mb-4">
                Verification builds<br />
                <span className="relative inline-block text-[hsl(237,52%,31%)]">
                  student trust.
                  <svg className="absolute -bottom-1 sm:-bottom-2 left-0 w-full" viewBox="0 0 270 12" fill="none" aria-hidden="true">
                    <path d="M4 9 C 67 3, 203 3, 266 9" stroke="hsl(39,96%,48%)" strokeWidth="4" strokeLinecap="round" />
                  </svg>
                </span>
              </h1>

              <p className="text-[#5A5E80] text-base sm:text-xl leading-relaxed max-w-2xl mb-8">
                Every teacher on BookMyCoaching is personally reviewed by our team before going live. Here's how the verification process works and what it means for you.
              </p>

              <div className="hidden sm:flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  className="rounded-2xl sm:rounded-full px-8 gap-2 bg-[hsl(237,52%,31%)] hover:bg-[hsl(237,52%,25%)] text-white shadow-xl shadow-[hsl(237,52%,31%)]/25 hover:-translate-y-0.5 active:scale-[0.98] transition-all"
                  onClick={() => navigate('/register')}
                >
                  Apply Now — it's free
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button size="lg" variant="outline" className="rounded-2xl sm:rounded-full px-8" asChild>
                  <Link to="/how-it-works/apply">See all steps →</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ── Process steps ── */}
        <section className="py-12 sm:py-24 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-6 sm:mb-14">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-[hsl(237,52%,50%)] mb-3 px-3 py-1 rounded-full bg-[hsl(237,52%,31%)]/10">
                The process
              </span>
              <h2 className="font-sans text-2xl sm:text-4xl text-[#262A7A] mb-2">Four steps to your Verified badge</h2>
              <p className="text-[#5A5E80] text-sm max-w-md mx-auto">Our verification is manual and personal — every application is reviewed by a real person.</p>
            </div>

            {/* Manual process notice */}
            <div className="flex gap-3 items-start rounded-2xl border border-[hsl(237,52%,31%)]/20 bg-[hsl(237,52%,31%)]/5 px-4 py-3.5 mb-6">
              <Info className="w-4 h-4 text-[hsl(237,52%,40%)] mt-0.5 shrink-0" />
              <p className="text-sm text-[#262A7A] leading-relaxed">
                Our verification process is currently manual — every teacher is personally contacted by our team to complete these steps. This typically takes a few business days.
              </p>
            </div>

            {/* Mobile: native grouped list */}
            <div className="sm:hidden bg-white rounded-2xl overflow-hidden border border-[#DCDDEE] shadow-sm">
              {steps.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div key={i} className={`flex items-start gap-4 px-4 py-4 ${i < steps.length - 1 ? 'border-b border-[#DCDDEE]' : ''}`}>
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${step.gradient} flex items-center justify-center shrink-0`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#A9ACD6]">Step {i + 1}</span>
                      <p className="font-semibold text-[#262A7A] text-sm leading-tight">{step.title}</p>
                      <p className="text-xs text-[#5A5E80] mt-0.5 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop: card layout */}
            <div className="hidden sm:block relative">
              <div className="absolute left-[2.25rem] top-14 bottom-14 w-px bg-gradient-to-b from-[hsl(237,52%,40%)]/40 via-[hsl(237,52%,40%)]/20 to-transparent" />
              <div className="flex flex-col gap-6">
                {steps.map((step, i) => (
                  <div key={i} className="flex gap-5 items-start group">
                    <div className="flex-shrink-0 z-10">
                      <div className={`w-[4.5rem] h-[4.5rem] rounded-2xl bg-gradient-to-br ${step.gradient} flex flex-col items-center justify-center shadow-lg`}>
                        <step.icon className="w-5 h-5 text-white mb-0.5" />
                        <span className="text-xs font-bold text-white/70 uppercase tracking-widest">Step {i + 1}</span>
                      </div>
                    </div>
                    <div className="flex-1 rounded-2xl border border-[#DCDDEE] bg-[#F7F7FC]/60 p-6 hover:shadow-md hover:-translate-y-0.5 hover:border-[#DCDDEE] transition-all duration-200">
                      <h3 className="font-semibold text-[#262A7A] text-base mb-1.5 group-hover:text-[hsl(237,52%,31%)] transition-colors">{step.title}</h3>
                      <p className="text-sm text-[#5A5E80] leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Benefits of verification ── */}
        <section className="py-10 sm:py-20 bg-[#F7F7FC]">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-6 sm:mb-10">
              <h2 className="font-sans text-xl sm:text-3xl text-[#262A7A] mb-2">What verification does for you</h2>
              <p className="text-[#5A5E80] text-sm">The Verified badge isn't just a label — it changes how students see and discover you.</p>
            </div>

            {/* Mobile: native list */}
            <div className="sm:hidden bg-white rounded-2xl overflow-hidden border border-[#DCDDEE] shadow-sm">
              {benefits.map((b, i) => (
                <div key={i} className={`flex items-start gap-4 px-4 py-4 ${i < benefits.length - 1 ? 'border-b border-[#DCDDEE]' : ''}`}>
                  <div className="w-9 h-9 rounded-xl bg-[hsl(237,52%,31%)]/10 flex items-center justify-center shrink-0">
                    <b.icon className="w-4 h-4 text-[hsl(237,52%,40%)]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#262A7A] text-sm leading-tight">{b.title}</p>
                    <p className="text-xs text-[#5A5E80] mt-0.5 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: card grid */}
            <div className="hidden sm:grid grid-cols-1 sm:grid-cols-3 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="flex gap-4 p-5 rounded-2xl bg-white border border-[#DCDDEE] hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                  <div className="w-10 h-10 rounded-xl bg-[hsl(237,52%,31%)]/10 flex items-center justify-center flex-shrink-0">
                    <b.icon className="w-5 h-5 text-[hsl(237,52%,40%)]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#262A7A] text-sm mb-1">{b.title}</p>
                    <p className="text-xs text-[#5A5E80] leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-12 sm:py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(237,52%,25%)] via-[hsl(237,52%,31%)] to-[hsl(237,52%,20%)]" />
          <div className="hidden sm:block absolute -top-20 -right-20 w-[400px] h-[400px] bg-white/5 rounded-full blur-[100px] pointer-events-none" />
          <div className="hidden sm:block absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-[hsl(39,96%,48%)]/10 rounded-full blur-[100px] pointer-events-none" />

          <div className="container mx-auto px-4 relative z-10 text-center">
            <div className="max-w-xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/90 text-sm font-medium mb-6 backdrop-blur-md">
                <Sparkles className="w-3.5 h-3.5 text-[hsl(39,96%,58%)]" />
                Free to apply — always
              </div>
              <h2 className="font-sans text-3xl sm:text-5xl text-white mb-4 leading-tight">
                Ready to get verified?
              </h2>
              <p className="text-indigo-200 text-base sm:text-lg mb-8 leading-relaxed">
                Create your profile in minutes. Our team will reach out to guide you through the rest.
              </p>
              <div className="hidden sm:flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  size="lg"
                  className="rounded-2xl sm:rounded-full px-10 py-6 bg-white text-[hsl(237,52%,31%)] hover:bg-white/90 shadow-xl shadow-black/20 hover:-translate-y-0.5 active:scale-[0.98] transition-all font-semibold"
                  onClick={() => navigate('/register')}
                >
                  Apply Now — it's free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" className="rounded-2xl sm:rounded-full px-10 py-6 border-white/30 text-white hover:bg-white/10" asChild>
                  <Link to="/how-it-works/apply">See the full process</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* Bottom spacer so content clears the sticky CTA bar on mobile */}
      <div className="sm:hidden h-20" />
      <Footer />

      {/* ── Sticky bottom CTA (mobile only) ── */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-t border-[#DCDDEE] px-4 py-3 shadow-[0_-8px_32px_rgba(0,0,0,0.08)]">
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/register')}
            className="touch-manipulation flex-1 bg-[hsl(237,52%,31%)] text-white font-bold text-sm py-4 rounded-2xl active:bg-[hsl(237,52%,22%)] active:scale-[0.98] transition-all shadow-lg shadow-[hsl(237,52%,31%)]/30"
          >
            Apply Now — it's free
          </button>
          <button
            onClick={() => navigate('/how-it-works/apply')}
            className="touch-manipulation flex-1 bg-[#EEEFF9] text-[#262A7A] font-bold text-sm py-4 rounded-2xl active:bg-slate-200 active:scale-[0.98] transition-all"
          >
            Full process →
          </button>
        </div>
      </div>
    </div>
  );
}
